import { useMemo } from "react";
import moment from "moment";
import { DATE_FORMATS } from "../../../../constants";
import {
  getPredictedCycles,
  getNormalizedLogs,
  getEffectiveEndDate,
  calculateAveragePeriodDuration,
  hasPeriodSettings,
} from "./menstrual-cycle.helper";

const { DATE_DAY_FORMAT, DATE_YEAR_FORMAT } = DATE_FORMATS;

const toMoment = (date) => moment(date, "YYYYMMDD");

/**
 * Hook: menstrual cycle and predicted cycle info for a given calendar day.
 * Used by DayNormal and DayEditor to show period day number or predicted period.
 *
 * @param {moment.Moment} date - The calendar day
 * @param {Array} femaleCycleLogs - Female cycle logs from locals
 * @param {Object} femaleCycleSettings - Female cycle settings from locals
 * @returns {{ menstrualCycleInfo: Object|null, predictedCycleInfo: Object|null }}
 */
export function useMenstrualCycleDayInfo(
  date,
  femaleCycleLogs,
  femaleCycleSettings
) {
  const menstrualCycleInfo = useMemo(() => {
    if (!femaleCycleLogs && !femaleCycleSettings) return null;

    const normalizedLogs = getNormalizedLogs(
      femaleCycleLogs,
      femaleCycleSettings
    );
    const defaultPeriodDuration = femaleCycleSettings?.averagePeriodLength ?? 5;
    const avgDuration =
      calculateAveragePeriodDuration(normalizedLogs, defaultPeriodDuration) ??
      defaultPeriodDuration;
    const currentDate = Number(date.format("YYYYMMDD"));

    const activePeriod = normalizedLogs.find((log) => {
      if (!log.startDate) return false;
      const startDate = log.startDate;
      const endDate =
        log.endDate || getEffectiveEndDate(log.startDate, avgDuration);
      return currentDate >= startDate && currentDate <= endDate;
    });

    if (!activePeriod) return null;

    const daysInPeriod =
      toMoment(currentDate).diff(toMoment(activePeriod.startDate), "days") + 1;

    return { dayNumber: daysInPeriod };
  }, [date, femaleCycleLogs, femaleCycleSettings]);

  const predictedCycleInfo = useMemo(() => {
    if (!hasPeriodSettings(femaleCycleSettings)) return null;

    const normalizedLogs = getNormalizedLogs(
      femaleCycleLogs,
      femaleCycleSettings
    );

    const currentDateNum = Number(date.format("YYYYMMDD"));
    const todayMonth = moment().startOf("month").format(DATE_YEAR_FORMAT);

    const predictions = getPredictedCycles(
      normalizedLogs,
      femaleCycleSettings,
      todayMonth,
      5
    );

    const matchingPrediction = predictions.find((pred) => {
      const startNum = Number(
        moment(pred.startDate, DATE_DAY_FORMAT).format("YYYYMMDD")
      );
      const endNum = Number(
        moment(pred.endDate, DATE_DAY_FORMAT).format("YYYYMMDD")
      );
      return currentDateNum >= startNum && currentDateNum <= endNum;
    });
    if (!matchingPrediction) return null;

    const daysInPeriod =
      toMoment(currentDateNum).diff(
        moment(matchingPrediction.startDate, DATE_DAY_FORMAT),
        "days"
      ) + 1;

    return { dayNumber: daysInPeriod, isPredicted: true };
  }, [date, femaleCycleLogs, femaleCycleSettings]);

  return { menstrualCycleInfo, predictedCycleInfo };
}
